/**
 * AdminLiveInventoryUpload
 * ------------------------
 * Admin panel: manage the "Weekly Deals · Hard-to-Find Live Inventory".
 * Each supplier partner (CIA Medical, Keystone Medical Supply, …) is
 * uploaded/replaced/deleted independently, and now carries an admin-set
 * REGION (Midwest, South, West Coast, …).
 *
 * The public /backorders/live-inventory page shows the merged union of
 * every source currently uploaded, but never exposes the supplier
 * partner's name — only the region gets displayed to hospitals.
 *
 * Backend rules (unchanged per source):
 *   - First column (source-distributor SKU) is dropped
 *   - Price / cost columns are dropped
 *   - Source-specific brand references auto-rebranded to "Sterile Mate LLC"
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, RefreshCw, Loader2, ExternalLink, ClipboardList,
  Trash2, CheckCircle2, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminLiveInventoryManual from "./AdminLiveInventoryManual";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLiveInventoryUpload({ token }) {
  const [loading, setLoading] = useState(true);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [savingRegionSlug, setSavingRegionSlug] = useState(null);
  const [sources, setSources] = useState([]);
  // Local drafts of the region dropdown so we don't hit the API on every keystroke
  const [regionDrafts, setRegionDrafts] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [publicSnapshot, setPublicSnapshot] = useState({ row_count: 0, updated_at: null });
  const inputRefs = useRef({});
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [srcs, pub, regs] = await Promise.all([
        axios.get(`${API}/api/admin/live-inventory/sources`, { headers }),
        axios.get(`${API}/api/live-inventory`),
        axios.get(`${API}/api/admin/live-inventory/regions`, { headers }),
      ]);
      const list = srcs.data?.sources || [];
      setSources(list);
      setRegionDrafts(Object.fromEntries(list.map((s) => [s.source, s.region || "National"])));
      setRegionOptions(regs.data?.regions || []);
      setPublicSnapshot({
        row_count: pub.data?.row_count || 0,
        updated_at: pub.data?.updated_at || null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const upload = async (slug, file) => {
    if (!file) return;
    const region = regionDrafts[slug] || "National";
    setUploadingSlug(slug);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/api/admin/live-inventory/upload?source=${encodeURIComponent(slug)}&region=${encodeURIComponent(region)}`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${r.data.source_label}: ${r.data.row_count} rows uploaded · Region: ${r.data.region}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setUploadingSlug(null);
      if (inputRefs.current[slug]) inputRefs.current[slug].value = "";
    }
  };

  const saveRegion = async (slug) => {
    const region = regionDrafts[slug];
    if (!region) return;
    setSavingRegionSlug(slug);
    try {
      await axios.put(
        `${API}/api/admin/live-inventory/sources/${encodeURIComponent(slug)}/region`,
        { region },
        { headers },
      );
      toast.success(`Region set to ${region}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSavingRegionSlug(null);
    }
  };

  const remove = async (slug, label) => {
    if (!window.confirm(`Delete the ${label} live-inventory list? Other supplier lists will remain untouched.`)) return;
    try {
      await axios.delete(`${API}/api/admin/live-inventory/sources/${slug}`, { headers });
      toast.success(`${label} list removed.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const totalRows = sources.reduce((s, x) => s + (x.row_count || 0), 0);
  const lastAny = sources
    .map((s) => s.updated_at)
    .filter(Boolean)
    .sort()
    .at(-1);
  const lastAnyStr = lastAny
    ? new Date(lastAny).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "Never";

  return (
    <div className="space-y-5" data-testid="admin-live-inventory">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-cyan-700" />
            Weekly Deals · Hard-to-Find Live Inventory
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            One list per supplier partner. Uploading a source replaces only that source&rsquo;s rows &mdash;
            other partners&rsquo; lists stay untouched. The public page shows the merged union and only
            ever displays each source&rsquo;s <b>region</b> — never the supplier name.
          </p>
        </div>
        <a
          href="/backorders/live-inventory"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-cyan-700 hover:text-cyan-900"
          data-testid="live-inv-open-public"
        >
          Open public page <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Aggregate snapshot */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Metric label="Total Public Rows" value={publicSnapshot.row_count || totalRows} />
        <Metric label="Supplier Sources" value={sources.filter((s) => s.row_count).length} />
        <Metric label="Last Updated" value={lastAnyStr} small />
      </div>

      {/* Per-source cards */}
      <div className="space-y-3">
        {loading && sources.length === 0 ? (
          <div className="text-sm text-slate-400 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading supplier sources…
          </div>
        ) : (
          sources.filter((s) => s.source !== "manual").map((s) => {
            const isUploaded = (s.row_count || 0) > 0;
            const busy = uploadingSlug === s.source;
            const savingRegion = savingRegionSlug === s.source;
            const draftRegion = regionDrafts[s.source] || "National";
            const regionDirty = isUploaded && draftRegion !== (s.region || "National");
            const updatedStr = s.updated_at
              ? new Date(s.updated_at).toLocaleString(undefined, {
                  year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                })
              : "Never";
            return (
              <div
                key={s.source}
                className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col gap-3"
                data-testid={`live-inv-source-${s.source}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[10px] font-black uppercase tracking-wider text-cyan-700">Supplier Source</p>
                      {isUploaded ? (
                        <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 ring-1 ring-emerald-500/40 px-1.5 py-0.5 rounded">
                          <CheckCircle2 className="w-3 h-3" /> Live
                        </span>
                      ) : (
                        <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 bg-slate-100 ring-1 ring-slate-300 px-1.5 py-0.5 rounded">
                          Empty
                        </span>
                      )}
                      {isUploaded && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-800 bg-cyan-50 ring-1 ring-cyan-500/40 px-2 py-0.5 rounded-full">
                          <MapPin className="w-3 h-3" /> Region: {s.region || "National"}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-extrabold text-slate-900 truncate">{s.source_label}</h3>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span><b className="text-slate-700">{s.row_count || 0}</b> rows</span>
                      <span>Updated: <b className="text-slate-700">{updatedStr}</b></span>
                      {s.source_filename && <span className="truncate">File: <b className="text-slate-700">{s.source_filename}</b></span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      ref={(el) => (inputRefs.current[s.source] = el)}
                      type="file"
                      accept=".csv,.xlsx,.xlsm"
                      className="hidden"
                      onChange={(e) => upload(s.source, e.target.files?.[0])}
                      data-testid={`live-inv-input-${s.source}`}
                    />
                    <Button
                      onClick={() => inputRefs.current[s.source]?.click()}
                      disabled={busy}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      size="sm"
                      data-testid={`live-inv-upload-${s.source}`}
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                      {busy ? "Uploading…" : isUploaded ? "Replace list" : "Upload list"}
                    </Button>
                    {isUploaded && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-500"
                        onClick={() => remove(s.source, s.source_label)}
                        data-testid={`live-inv-delete-${s.source}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Region selector row */}
                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <label
                    htmlFor={`region-${s.source}`}
                    className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1"
                  >
                    <MapPin className="w-3.5 h-3.5 text-cyan-700" />
                    Public region
                  </label>
                  <select
                    id={`region-${s.source}`}
                    value={draftRegion}
                    onChange={(e) => setRegionDrafts((prev) => ({ ...prev, [s.source]: e.target.value }))}
                    className="text-sm rounded-md border border-slate-300 bg-white px-3 py-1.5 focus:ring-cyan-500 focus:border-cyan-500"
                    data-testid={`live-inv-region-select-${s.source}`}
                  >
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {isUploaded && regionDirty && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500 text-amber-800 hover:bg-amber-50"
                      onClick={() => saveRegion(s.source)}
                      disabled={savingRegion}
                      data-testid={`live-inv-region-save-${s.source}`}
                    >
                      {savingRegion ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                      Save region
                    </Button>
                  )}
                  <span className="text-[11px] text-slate-500 ml-auto">
                    This is what hospitals see for every row from this source.
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <AdminLiveInventoryManual token={token} onChanged={load} />

      <div className="flex items-center justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="live-inv-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
    </div>
  );
}

function Metric({ label, value, small }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-black text-slate-900 ${small ? "text-sm" : "text-2xl"}`}>{value}</p>
    </div>
  );
}
